import React from 'react'
import { getTranslation } from '../translations'

// Import all crop tip JSON files
import riceData from '../assets/crop-tips/rice.json'
import wheatData from '../assets/crop-tips/wheat.json'
import maizeData from '../assets/crop-tips/maize.json'
import sugarcaneData from '../assets/crop-tips/sugarcane.json'
import mustardData from '../assets/crop-tips/mustard.json'
import potatoData from '../assets/crop-tips/potato.json'

const cropDataMap = {
  rice: riceData,
  wheat: wheatData,
  maize: maizeData,
  sugarcane: sugarcaneData,
  mustard: mustardData,
  potato: potatoData,
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="bg-[#134225] text-white font-extrabold px-3 py-2 uppercase tracking-wide text-sm">{title}</div>
      {subtitle ? (
        <div className="text-[12px] italic text-[#334c3e] px-2 py-1">{subtitle}</div>
      ) : null}
    </div>
  )
}

function StageRow({ stage, practices, isFirst }) {
  return (
    <>
      {practices.map((practice, idx) => (
        <tr key={`${stage}-${idx}`} className="odd:bg-white even:bg-gray-50 text-sm">
          {idx === 0 && (
            <td 
              className="px-3 py-3 border border-[#e4ebe7] font-bold align-top" 
              rowSpan={practices.length}
            >
              {stage}
            </td>
          )}
          <td className="px-3 py-3 border border-[#e4ebe7]">{practice.practice}</td>
          <td className="px-3 py-3 border border-[#e4ebe7]">{practice.why}</td>
        </tr>
      ))}
    </>
  )
}

export default function CropSpecificTips({
  cropName,
  language = 'english',
  title,
  subtitle,
}) {
  const t = getTranslation(language)
  const sectionTitle = title || t.section5Title
  const sectionSubtitle = subtitle || t.section5Subtitle
  
  // Get crop data
  const cropData = cropDataMap[cropName?.toLowerCase()]
  
  // Check if crop data exists and has proper structure
  const hasValidTips = cropData && 
    cropData.tips && 
    (cropData.tips[language.toLowerCase()] || cropData.tips.english) &&
    Array.isArray(cropData.tips[language.toLowerCase()] || cropData.tips.english)
  
  // If crop not found or invalid structure, show error message
  if (!cropData || !hasValidTips) {
    return (
      <div className="mt-6">
        <SectionHeader title={sectionTitle} subtitle={sectionSubtitle} />
        <div className="border border-[#cbd9d2] rounded-md overflow-hidden bg-yellow-50">
          <div className="px-6 py-8 text-center">
            <div className="text-yellow-800 font-semibold text-lg mb-2">
              {t.cropTipsNotAvailable}
            </div>
            <p className="text-yellow-700 text-sm">
              {t.cropTipsNotAvailableDesc.replace('{crop}', cropName || 'Unknown')}
            </p>
            <p className="text-yellow-600 text-xs mt-3">
              {t.availableCrops}: {Object.keys(cropDataMap).join(', ')}
            </p>
          </div>
        </div>
      </div>
    )
  }
  
  // Get tips in specified language, fallback to english
  const tips = cropData.tips[language.toLowerCase()] || cropData.tips.english || []
  
  // Sort stages by stageOrder
  const sortedStages = [...tips].sort((a, b) => a.stageOrder - b.stageOrder)
  
  return (
    <div className="mt-6">
      <SectionHeader title={sectionTitle} subtitle={sectionSubtitle} />
      <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#134225] text-white text-sm">
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.stage}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.practice}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.why}</th>
            </tr>
          </thead>
          <tbody>
            {sortedStages.map((stage, i) => (
              <StageRow 
                key={stage.stageName} 
                stage={stage.stageName} 
                practices={stage.practices} 
                isFirst={i === 0} 
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
